import { appIntl } from '@nxsys/helpers/Common';
import { PaginationType } from '@nxsys/types/models/Common';
import { IEmploymentIntermediary } from '@nxsys/types/models/employee';
import { P30P32RecordType } from '@nxsys/types/models/hmrcReports';
import { DraftPayroll } from '@nxsys/types/models/payroll';
import { showToast } from '@nxsys/utils/toast';
import { createSlice } from '@reduxjs/toolkit';
import { Payroll } from '../../revenueFilings/types';
import { reportActs } from '../actions';
import {
  GetAutoEnrollmentStatusListParams,
  GetAWRListParams,
  GetAttachmentOrdersReportListParams,
  GetBankFilesReportParams,
  GetOnHoldWorkerReportListParams,
  GetCISStateOfEarningsListParams,
  GetDormantWorkerListParams,
  GetDraftPayrollListParams,
  GetEmploymentIntermediaryListParams,
  GetFiftyTwoWeekAverageSummaryListParams,
  GetHolidayPayBalanceDetailListParams,
  GetHolidayPayBalanceSummaryListParams,
  GetNetPayListParams,
  GetP30ReportParams,
  GetP32ReportParams,
  GetPayrollListParams,
  GetPendingPensionContributionReportListParams,
  GetPensionContributionReportListParams,
  GetRTWListParams,
  GetWTRListParams,
} from '../types';
import { BankFilesType } from '@nxsys/types/models/bankFilesReports/bankfiles';
import { IAttachmentOrder } from '@nxsys/types/models/attachmentOrder';
import { EmployeeRecordType } from '@nxsys/types/models/employee';

export type ReportState = {
  draftPayrollList: {
    loading: boolean;
    list: DraftPayroll[];
    pagination: PaginationType;
    queryParams: GetDraftPayrollListParams | null;
  };
  payrollList: {
    loading: boolean;
    list: Payroll[];
    pagination: PaginationType;
    queryParams: GetPayrollListParams | null;
  };
  holidayPayBalanceDetailList: {
    loading: boolean;
    list: IHolidayPayBalance[];
    pagination: PaginationType;
    queryParams: GetHolidayPayBalanceDetailListParams | null;
  };
  holidayPayBalanceSummaryList: {
    loading: boolean;
    list: IHolidayPayBalanceSummary[];
    pagination: PaginationType;
    queryParams: GetHolidayPayBalanceSummaryListParams | null;
  };
  netPayList: {
    loading: boolean;
    list: INetPay[];
    pagination: PaginationType;
    queryParams: GetNetPayListParams | null;
  };
  fiftyTwoWeekAverageSummaryList: {
    loading: boolean;
    list: IFiftyTwoWeekAverageSummary[];
    pagination: PaginationType;
    queryParams: GetFiftyTwoWeekAverageSummaryListParams | null;
  };
  rtwList: {
    loading: boolean;
    list: IRTW[];
    pagination: PaginationType;
    queryParams: GetRTWListParams | null;
  };
  awrList: {
    loading: boolean;
    list: IAWR[];
    pagination: PaginationType;
    queryParams: GetAWRListParams | null;
  };
  wtrList: {
    loading: boolean;
    list: IWTR[];
    pagination: PaginationType;
    queryParams: GetWTRListParams | null;
  };
  cisStateOfEarningsList: {
    loading: boolean;
    list: IEarningReport[];
    pagination: PaginationType;
    queryParams: GetCISStateOfEarningsListParams | null;
  };
  dormantWorkerList: {
    loading: boolean;
    list: IDormantWorker[];
    pagination: PaginationType;
    queryParams: GetDormantWorkerListParams | null;
  };
  employmentIntermediaryList: {
    loading: boolean;
    list: IEmploymentIntermediary[];
    pagination: PaginationType;
    queryParams: GetEmploymentIntermediaryListParams | null;
  };
  autoEnrollmentStatusList: {
    loading: boolean;
    list: IPensionAssessed[];
    pagination: PaginationType;
    queryParams: GetAutoEnrollmentStatusListParams | null;
  };
  pensionContributionReportList: {
    loading: boolean;
    list: IPensionSubmission[];
    pagination: PaginationType;
    queryParams: GetPensionContributionReportListParams | null;
  };
  pendingPensionContributionReportList: {
    loading: boolean;
    list: Payroll[];
    pagination: PaginationType;
    queryParams: GetPendingPensionContributionReportListParams | null;
  };
  p30ReportList: {
    loading: boolean;
    list: P30P32RecordType[];
    pagination: PaginationType;
    queryParams: GetP30ReportParams | null;
  };
  p32ReportList: {
    loading: boolean;
    list: P30P32RecordType[];
    pagination: PaginationType;
    queryParams: GetP32ReportParams | null;
  };
  bankFilesReportList: {
    loading: boolean;
    list: BankFilesType[];
    pagination: PaginationType;
    queryParams: GetBankFilesReportParams | null;
  };
  attachmentOrdersReportList: {
    loading: boolean;
    list: IAttachmentOrder[];
    pagination: PaginationType;
    queryParams: GetAttachmentOrdersReportListParams | null;
  };
  onHoldWorkerReportList: {
    loading: boolean;
    list: EmployeeRecordType[];
    pagination: PaginationType;
    queryParams: GetOnHoldWorkerReportListParams | null;
  };
};

const initialState: ReportState = {
  draftPayrollList: {
    loading: false,
    list: [],
    pagination: {
      current: 1,
      pageSize: 20,
      totalDocs: 0,
    },
    queryParams: null,
  },
  payrollList: {
    loading: false,
    list: [],
    pagination: {
      current: 1,
      pageSize: 20,
      totalDocs: 0,
    },
    queryParams: null,
  },
  holidayPayBalanceDetailList: {
    loading: false,
    list: [],
    pagination: {
      current: 1,
      pageSize: 20,
      totalDocs: 0,
    },
    queryParams: null,
  },
  holidayPayBalanceSummaryList: {
    loading: false,
    list: [],
    pagination: {
      current: 1,
      pageSize: 20,
      totalDocs: 0,
    },
    queryParams: null,
  },
  netPayList: {
    loading: false,
    list: [],
    pagination: {
      current: 1,
      pageSize: 20,
      totalDocs: 0,
    },
    queryParams: null,
  },
  fiftyTwoWeekAverageSummaryList: {
    loading: false,
    list: [],
    pagination: {
      current: 1,
      pageSize: 20,
      totalDocs: 0,
    },
    queryParams: null,
  },
  rtwList: {
    loading: false,
    list: [],
    pagination: {
      current: 1,
      pageSize: 20,
      totalDocs: 0,
    },
    queryParams: null,
  },
  awrList: {
    loading: false,
    list: [],
    pagination: {
      current: 1,
      pageSize: 20,
      totalDocs: 0,
    },
    queryParams: null,
  },
  wtrList: {
    loading: false,
    list: [],
    pagination: {
      current: 1,
      pageSize: 20,
      totalDocs: 0,
    },
    queryParams: null,
  },
  cisStateOfEarningsList: {
    loading: false,
    list: [],
    pagination: {
      current: 1,
      pageSize: 20,
      totalDocs: 0,
    },
    queryParams: null,
  },
  dormantWorkerList: {
    loading: false,
    list: [],
    pagination: {
      current: 1,
      pageSize: 20,
      totalDocs: 0,
    },
    queryParams: null,
  },
  employmentIntermediaryList: {
    loading: false,
    list: [],
    pagination: {
      current: 1,
      pageSize: 20,
      totalDocs: 0,
    },
    queryParams: null,
  },
  autoEnrollmentStatusList: {
    loading: false,
    list: [],
    pagination: {
      current: 1,
      pageSize: 20,
      totalDocs: 0,
    },
    queryParams: null,
  },
  pensionContributionReportList: {
    loading: false,
    list: [],
    pagination: {
      current: 1,
      pageSize: 20,
      totalDocs: 0,
    },
    queryParams: null,
  },
  pendingPensionContributionReportList: {
    loading: false,
    list: [],
    pagination: {
      current: 1,
      pageSize: 20,
      totalDocs: 0,
    },
    queryParams: null,
  },
  p30ReportList: {
    loading: false,
    list: [],
    pagination: {
      current: 1,
      pageSize: 20,
      totalDocs: 0,
    },
    queryParams: null,
  },
  p32ReportList: {
    loading: false,
    list: [],
    pagination: {
      current: 1,
      pageSize: 20,
      totalDocs: 0,
    },
    queryParams: null,
  },
  bankFilesReportList: {
    loading: false,
    list: [],
    pagination: {
      current: 1,
      pageSize: 20,
      totalDocs: 0,
    },
    queryParams: null,
  },
  attachmentOrdersReportList: {
    loading: false,
    list: [],
    pagination: {
      current: 1,
      pageSize: 20,
      totalDocs: 0,
    },
    queryParams: null,
  },
  onHoldWorkerReportList: {
    loading: false,
    list: [],
    pagination: {
      current: 1,
      pageSize: 20,
      totalDocs: 0,
    },
    queryParams: null,
  },
};

const reportSlice = createSlice({
  name: 'report',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    // Get Draft Payroll List Records
    builder.addCase(reportActs.getDraftPayrollList.pending, (state) => {
      state.draftPayrollList.loading = true;
    });
    builder.addCase(
      reportActs.getDraftPayrollList.fulfilled,
      (state, action) => {
        state.draftPayrollList.loading = false;
        state.draftPayrollList.list = action.payload.data;
        state.draftPayrollList.pagination = action.payload.pagination;
        state.draftPayrollList.queryParams = (
          action.payload as any
        ).queryParams;
      },
    );
    builder.addCase(reportActs.getDraftPayrollList.rejected, (state) => {
      state.draftPayrollList.loading = false;
      showToast({
        type: 'error',
        message: appIntl().formatMessage({
          id: 'report.payrollDraft.getListError',
        }),
      });
    });

    // Get Payroll List Records
    builder.addCase(reportActs.getPayrollList.pending, (state) => {
      state.payrollList.loading = true;
    });
    builder.addCase(reportActs.getPayrollList.fulfilled, (state, action) => {
      state.payrollList.loading = false;
      state.payrollList.list = action.payload.data;
      state.payrollList.pagination = action.payload.pagination;
      state.payrollList.queryParams = (action.payload as any).queryParams;
    });
    builder.addCase(reportActs.getPayrollList.rejected, (state) => {
      state.payrollList.loading = false;
      showToast({
        type: 'error',
        message: appIntl().formatMessage({
          id: 'report.payroll.getListError',
        }),
      });
    });

    // Get Holiday Pay Balance Detail List Records
    builder.addCase(
      reportActs.getHolidayPayBalanceDetailList.pending,
      (state) => {
        state.holidayPayBalanceDetailList.loading = true;
      },
    );
    builder.addCase(
      reportActs.getHolidayPayBalanceDetailList.fulfilled,
      (state, action) => {
        state.holidayPayBalanceDetailList.loading = false;
        state.holidayPayBalanceDetailList.list = action.payload.data;
        state.holidayPayBalanceDetailList.pagination =
          action.payload.pagination;
        state.holidayPayBalanceDetailList.queryParams = (
          action.payload as any
        ).queryParams;
      },
    );
    builder.addCase(
      reportActs.getHolidayPayBalanceDetailList.rejected,
      (state) => {
        state.holidayPayBalanceDetailList.loading = false;
        showToast({
          type: 'error',
          message: appIntl().formatMessage({
            id: 'report.holidayPayBalanceDetail.getListError',
          }),
        });
      },
    );

    // Get Holiday Pay Balance Summary List Records
    builder.addCase(
      reportActs.getHolidayPayBalanceSummaryList.pending,
      (state) => {
        state.holidayPayBalanceSummaryList.loading = true;
      },
    );
    builder.addCase(
      reportActs.getHolidayPayBalanceSummaryList.fulfilled,
      (state, action) => {
        state.holidayPayBalanceSummaryList.loading = false;
        state.holidayPayBalanceSummaryList.list = action.payload.data;
        state.holidayPayBalanceSummaryList.pagination =
          action.payload.pagination;
        state.holidayPayBalanceSummaryList.queryParams = (
          action.payload as any
        ).queryParams;
      },
    );
    builder.addCase(
      reportActs.getHolidayPayBalanceSummaryList.rejected,
      (state) => {
        state.holidayPayBalanceSummaryList.loading = false;
        showToast({
          type: 'error',
          message: appIntl().formatMessage({
            id: 'report.holidayPayBalanceSummary.getListError',
          }),
        });
      },
    );

    // Get Net Pay List Records
    builder.addCase(reportActs.getNetPayList.pending, (state) => {
      state.netPayList.loading = true;
    });
    builder.addCase(reportActs.getNetPayList.fulfilled, (state, action) => {
      state.netPayList.loading = false;
      state.netPayList.list = action.payload.data;
      state.netPayList.pagination = action.payload.pagination;
      state.netPayList.queryParams = (action.payload as any).queryParams;
    });
    builder.addCase(reportActs.getNetPayList.rejected, (state) => {
      state.netPayList.loading = false;
      showToast({
        type: 'error',
        message: appIntl().formatMessage({
          id: 'report.netPay.getListError',
        }),
      });
    });

    // Get Fifty Two Week Average Summary List Records
    builder.addCase(
      reportActs.getFiftyTwoWeekAverageSummaryList.pending,
      (state) => {
        state.fiftyTwoWeekAverageSummaryList.loading = true;
      },
    );
    builder.addCase(
      reportActs.getFiftyTwoWeekAverageSummaryList.fulfilled,
      (state, action) => {
        state.fiftyTwoWeekAverageSummaryList.loading = false;
        state.fiftyTwoWeekAverageSummaryList.list = action.payload.data;
        state.fiftyTwoWeekAverageSummaryList.pagination =
          action.payload.pagination;
        state.fiftyTwoWeekAverageSummaryList.queryParams = (
          action.payload as any
        ).queryParams;
      },
    );
    builder.addCase(
      reportActs.getFiftyTwoWeekAverageSummaryList.rejected,
      (state) => {
        state.fiftyTwoWeekAverageSummaryList.loading = false;
        showToast({
          type: 'error',
          message: appIntl().formatMessage({
            id: 'report.fiftyTwoWeekAverageSummary.getListError',
          }),
        });
      },
    );

    // Get RTW List Records
    builder.addCase(reportActs.getRTWList.pending, (state) => {
      state.rtwList.loading = true;
    });
    builder.addCase(reportActs.getRTWList.fulfilled, (state, action) => {
      state.rtwList.loading = false;
      state.rtwList.list = action.payload.data;
      state.rtwList.pagination = action.payload.pagination;
      state.rtwList.queryParams = (action.payload as any).queryParams;
    });
    builder.addCase(reportActs.getRTWList.rejected, (state) => {
      state.rtwList.loading = false;
      showToast({
        type: 'error',
        message: appIntl().formatMessage({
          id: 'report.rtw.getListError',
        }),
      });
    });

    // Get AWR List Records
    builder.addCase(reportActs.getAWRList.pending, (state) => {
      state.awrList.loading = true;
    });
    builder.addCase(reportActs.getAWRList.fulfilled, (state, action) => {
      state.awrList.loading = false;
      state.awrList.list = action.payload.data;
      state.awrList.pagination = action.payload.pagination;
      state.awrList.queryParams = (action.payload as any).queryParams;
    });
    builder.addCase(reportActs.getAWRList.rejected, (state) => {
      state.awrList.loading = false;
      showToast({
        type: 'error',
        message: appIntl().formatMessage({
          id: 'report.awr.getListError',
        }),
      });
    });

    // Get WTR List Records
    builder.addCase(reportActs.getWTRList.pending, (state) => {
      state.wtrList.loading = true;
    });
    builder.addCase(reportActs.getWTRList.fulfilled, (state, action) => {
      state.wtrList.loading = false;
      state.wtrList.list = action.payload.data;
      state.wtrList.pagination = action.payload.pagination;
      state.wtrList.queryParams = (action.payload as any).queryParams;
    });
    builder.addCase(reportActs.getWTRList.rejected, (state) => {
      state.wtrList.loading = false;
      showToast({
        type: 'error',
        message: appIntl().formatMessage({
          id: 'report.wtr.getListError',
        }),
      });
    });

    // Get CIS Statement of Earnings List Records
    builder.addCase(reportActs.getCISStateOfEarningsList.pending, (state) => {
      state.cisStateOfEarningsList.loading = true;
    });
    builder.addCase(
      reportActs.getCISStateOfEarningsList.fulfilled,
      (state, action) => {
        state.cisStateOfEarningsList.loading = false;
        state.cisStateOfEarningsList.list = action.payload.data;
        state.cisStateOfEarningsList.pagination = action.payload.pagination;
        state.cisStateOfEarningsList.queryParams = (
          action.payload as any
        ).queryParams;
      },
    );
    builder.addCase(reportActs.getCISStateOfEarningsList.rejected, (state) => {
      state.cisStateOfEarningsList.loading = false;
      showToast({
        type: 'error',
        message: appIntl().formatMessage({
          id: 'report.cisStateOfEarnings.getListError',
        }),
      });
    });

    // Get Dormant Worker List Records
    builder.addCase(reportActs.getDormantWorkerList.pending, (state) => {
      state.dormantWorkerList.loading = true;
    });
    builder.addCase(
      reportActs.getDormantWorkerList.fulfilled,
      (state, action) => {
        state.dormantWorkerList.loading = false;
        state.dormantWorkerList.list = action.payload.data;
        state.dormantWorkerList.pagination = action.payload.pagination;
        state.dormantWorkerList.queryParams = (
          action.payload as any
        ).queryParams;
      },
    );
    builder.addCase(reportActs.getDormantWorkerList.rejected, (state) => {
      state.dormantWorkerList.loading = false;
      showToast({
        type: 'error',
        message: appIntl().formatMessage({
          id: 'report.dormantWorker.getListError',
        }),
      });
    });

    // Get Employment Intermediary List Records
    builder.addCase(
      reportActs.getEmploymentIntermediaryList.pending,
      (state) => {
        state.employmentIntermediaryList.loading = true;
      },
    );
    builder.addCase(
      reportActs.getEmploymentIntermediaryList.fulfilled,
      (state, action) => {
        state.employmentIntermediaryList.loading = false;
        state.employmentIntermediaryList.list = action.payload.data;
        state.employmentIntermediaryList.pagination = action.payload.pagination;
        state.employmentIntermediaryList.queryParams = (
          action.payload as any
        ).queryParams;
      },
    );
    builder.addCase(
      reportActs.getEmploymentIntermediaryList.rejected,
      (state) => {
        state.employmentIntermediaryList.loading = false;
        showToast({
          type: 'error',
          message: appIntl().formatMessage({
            id: 'report.employmentIntermediary.getListError',
          }),
        });
      },
    );

    // Get Employment Intermediary List Records
    builder.addCase(
      reportActs.getEmploymentIntermediaryInAgencyPortalList.pending,
      (state) => {
        state.employmentIntermediaryList.loading = true;
      },
    );
    builder.addCase(
      reportActs.getEmploymentIntermediaryInAgencyPortalList.fulfilled,
      (state, action) => {
        state.employmentIntermediaryList.loading = false;
        state.employmentIntermediaryList.list = action.payload.data;
        state.employmentIntermediaryList.pagination = action.payload.pagination;
        state.employmentIntermediaryList.queryParams = (
          action.payload as any
        ).queryParams;
      },
    );
    builder.addCase(
      reportActs.getEmploymentIntermediaryInAgencyPortalList.rejected,
      (state) => {
        state.employmentIntermediaryList.loading = false;
        showToast({
          type: 'error',
          message: appIntl().formatMessage({
            id: 'report.employmentIntermediary.getListError',
          }),
        });
      },
    );

    // Get Auto Enrollment Status List Records
    builder.addCase(reportActs.getAutoEnrollmentStatusList.pending, (state) => {
      state.employmentIntermediaryList.loading = true;
    });
    builder.addCase(
      reportActs.getAutoEnrollmentStatusList.fulfilled,
      (state, action) => {
        state.autoEnrollmentStatusList.loading = false;
        state.autoEnrollmentStatusList.list = action.payload.data;
        state.autoEnrollmentStatusList.pagination = action.payload.pagination;
        state.autoEnrollmentStatusList.queryParams = (
          action.payload as any
        ).queryParams;
      },
    );
    builder.addCase(
      reportActs.getAutoEnrollmentStatusList.rejected,
      (state) => {
        state.autoEnrollmentStatusList.loading = false;
        showToast({
          type: 'error',
          message: appIntl().formatMessage({
            id: 'report.autoEnrollmentStatus.getListError',
          }),
        });
      },
    );

    // Get Pension Contribution Report List Records
    builder.addCase(
      reportActs.getPensionContributionReportList.pending,
      (state) => {
        state.pensionContributionReportList.loading = true;
      },
    );
    builder.addCase(
      reportActs.getPensionContributionReportList.fulfilled,
      (state, action) => {
        state.pensionContributionReportList.loading = false;
        state.pensionContributionReportList.list = action.payload.data;
        state.pensionContributionReportList.pagination =
          action.payload.pagination;
        state.pensionContributionReportList.queryParams = (
          action.payload as any
        ).queryParams;
      },
    );
    builder.addCase(
      reportActs.getPensionContributionReportList.rejected,
      (state) => {
        state.pensionContributionReportList.loading = false;
        showToast({
          type: 'error',
          message: appIntl().formatMessage({
            id: 'report.pensionContributionReport.getListError',
          }),
        });
      },
    );

    // Get Pending Pension Contribution Report List Records
    builder.addCase(
      reportActs.getPendingPensionContributionReportList.pending,
      (state) => {
        state.pendingPensionContributionReportList.loading = true;
      },
    );
    builder.addCase(
      reportActs.getPendingPensionContributionReportList.fulfilled,
      (state, action) => {
        state.pendingPensionContributionReportList.loading = false;
        state.pendingPensionContributionReportList.list = action.payload.data;
        state.pendingPensionContributionReportList.pagination =
          action.payload.pagination;
        state.pendingPensionContributionReportList.queryParams = (
          action.payload as any
        ).queryParams;
      },
    );
    builder.addCase(
      reportActs.getPendingPensionContributionReportList.rejected,
      (state) => {
        state.pendingPensionContributionReportList.loading = false;
        showToast({
          type: 'error',
          message: appIntl().formatMessage({
            id: 'report.pendingPensionContributionReport.getListError',
          }),
        });
      },
    );

    // Get P30 Report List Records
    builder.addCase(reportActs.getP30ReportList.pending, (state) => {
      state.p30ReportList.loading = true;
    });
    builder.addCase(reportActs.getP30ReportList.fulfilled, (state, action) => {
      state.p30ReportList.loading = false;
      state.p30ReportList.list = action.payload.data;
      state.p30ReportList.pagination = action.payload.pagination;
      state.p30ReportList.queryParams = (action.payload as any).queryParams;
    });
    builder.addCase(reportActs.getP30ReportList.rejected, (state) => {
      state.p30ReportList.loading = false;
      showToast({
        type: 'error',
        message: appIntl().formatMessage({
          id: 'report.p30Report.getListError',
        }),
      });
    });

    // Get P32 Report List Records
    builder.addCase(reportActs.getP32ReportList.pending, (state) => {
      state.p32ReportList.loading = true;
    });
    builder.addCase(reportActs.getP32ReportList.fulfilled, (state, action) => {
      state.p32ReportList.loading = false;
      state.p32ReportList.list = action.payload.data;
      state.p32ReportList.pagination = action.payload.pagination;
      state.p32ReportList.queryParams = (action.payload as any).queryParams;
    });
    builder.addCase(reportActs.getP32ReportList.rejected, (state) => {
      state.p32ReportList.loading = false;
      showToast({
        type: 'error',
        message: appIntl().formatMessage({
          id: 'report.p32Report.getListError',
        }),
      });
    });

    // Get Bank Files Report List Records
    builder.addCase(reportActs.getBankFilesReportList.pending, (state) => {
      state.bankFilesReportList.loading = true;
    });
    builder.addCase(
      reportActs.getBankFilesReportList.fulfilled,
      (state, action) => {
        state.bankFilesReportList.loading = false;
        state.bankFilesReportList.list = action.payload.data;
        state.bankFilesReportList.pagination = action.payload.pagination;
        state.bankFilesReportList.queryParams = (
          action.payload as any
        ).queryParams;
      },
    );
    builder.addCase(reportActs.getBankFilesReportList.rejected, (state) => {
      state.bankFilesReportList.loading = false;
      showToast({
        type: 'error',
        message: appIntl().formatMessage({
          id: 'report.bankFile.getListError',
        }),
      });
    });

    // Get Attachment Orders Report List Records
    builder.addCase(
      reportActs.getAttachmentOrdersReportList.pending,
      (state) => {
        state.attachmentOrdersReportList.loading = true;
      },
    );
    builder.addCase(
      reportActs.getAttachmentOrdersReportList.fulfilled,
      (state, action) => {
        state.attachmentOrdersReportList.loading = false;
        state.attachmentOrdersReportList.list = action.payload.data;
        state.attachmentOrdersReportList.pagination = action.payload.pagination;
        state.attachmentOrdersReportList.queryParams = (
          action.payload as any
        ).queryParams;
      },
    );
    builder.addCase(
      reportActs.getAttachmentOrdersReportList.rejected,
      (state) => {
        state.attachmentOrdersReportList.loading = false;
        showToast({
          type: 'error',
          message: appIntl().formatMessage({
            id: 'report.attachmentOrders.getListError',
          }),
        });
      },
    );

    // Get On Hold Worker Report List Records
    builder.addCase(reportActs.getOnHoldWorkerReportList.pending, (state) => {
      state.onHoldWorkerReportList.loading = true;
    });
    builder.addCase(
      reportActs.getOnHoldWorkerReportList.fulfilled,
      (state, action) => {
        state.onHoldWorkerReportList.loading = false;
        state.onHoldWorkerReportList.list = action.payload.data;
        state.onHoldWorkerReportList.pagination = action.payload.pagination;
        state.onHoldWorkerReportList.queryParams = (
          action.payload as any
        ).queryParams;
      },
    );
    builder.addCase(reportActs.getOnHoldWorkerReportList.rejected, (state) => {
      state.onHoldWorkerReportList.loading = false;
      showToast({
        type: 'error',
        message: appIntl().formatMessage({
          id: 'report.onHoldWorker.getListError',
        }),
      });
    });
  },
});

export const reportReducer = reportSlice.reducer;
export const reportActions = {
  ...reportSlice.actions,
  ...reportActs,
};
